import * as A from "@radix-ui/react-alert-dialog"
import { tv } from "@utils/tw"
import { isValidElement, ReactElement, ReactNode, useState } from "react"
import { Button } from "./Button"
import { ButtonGroup } from "./ButtonGroup"

export type AlertDialogProps = {
  title: ReactNode
  /**
   * The written description of the trigger element.
   */
  description?: ReactNode
  canDismiss?: boolean
  onCancel?: (e: Event) => void
  open?: boolean
  /**
   * Triggered when the user confirms the action.
   *
   * Return a promise to wait for an async action to complete.
   *
   * If the promise resolves to `false`, the dialog will not close.
   * @returns
   */
  onConfirm?: () => void | boolean | Promise<void | boolean>
  /**
   * This can either be a string (for the label), or a `<Button />` element, if you'd like to customize the button.
   * Use `onCancel` rather than a click handler
   */
  cancelButton?: string | ReactElement | boolean
  /**
   * This can either be a string (for the label), or a `<Button />` element, if you'd like to customize the button.
   * Use `onConfirm` rather than a click event
   */
  confirmButton?: string | ReactElement
  onOpenChange?: (open: boolean) => void
  /**
   * The trigger element that will open the dialog when clicked.
   */
  trigger?: ReactNode
  /** The contents of the dialog */
  children?: ReactNode
  /**
   * Whether the dialog should be open or not. Use this when no trigger is provided.
   */
  forceOpen?: boolean
}

/**
 * An alert dialog is a modal dialog that asks the user to confirm an action, make a choice, or acknowledge a message.
 *
 * It should always have a title, and usually a description.
 *
 * It looks a lot like a modal, but it's more focused on communication/confirmation.
 */
export function AlertDialog(props: AlertDialogProps) {
  const [open, setOpen] = useState(props.forceOpen ?? props.open ?? false)
  const [confirming, setConfirming] = useState(false)
  const styles = alertStyles()

  const requestCancel = (e: Event) => {
    if (props.forceOpen) {
      e.preventDefault()
      return
    }
    props.onCancel?.(e as any)
    if (!e.defaultPrevented) {
      setOpen(false)
    }
    props.onOpenChange?.(false)
  }

  const requestConfirm = async (e: Event) => {
    e.preventDefault()
    const result = props.onConfirm?.()
    let shouldClose = true
    if (result && result instanceof Promise) {
      setConfirming(true)
      try {
        shouldClose = (await result) === false ? false : true
      } finally {
        setConfirming(false)
      }
    } else if (typeof result === "boolean") {
      shouldClose = result
    }
    if (shouldClose) {
      setOpen(false)
      props.onOpenChange?.(false)
    }
  }

  return (
    <A.Root
      open={open === true || props.forceOpen === true}
      onOpenChange={(open) => {
        if (props.forceOpen) return
        props.onOpenChange?.(open)
        setOpen(open)
      }}
    >
      {props.trigger && <A.Trigger asChild>{props.trigger}</A.Trigger>}
      <A.Portal>
        <A.Overlay
          className={styles.overlay()}
          onClick={(e) => {
            if (!props.canDismiss) {
              return e.preventDefault()
            }
            requestCancel(e as any)
          }}
        />
        <A.Content
          asChild
          className={styles.content()}
          onEscapeKeyDown={(e) => {
            requestCancel(e)
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              requestConfirm(e as any)
            }}
          >
            <div className={styles.header()}>
              <A.Title className={styles.title()}>{props.title}</A.Title>
              {props.description && <A.Description className={styles.description()}>{props.description}</A.Description>}
            </div>
            {props.children && <div className={styles.inner()}>{props.children}</div>}
            <div className={styles.footer()}>
              <ButtonGroup variant="dialog">
                {props.cancelButton && (
                  <A.Cancel asChild onClick={requestCancel as any}>
                    {isValidElement(props.cancelButton) ? (
                      props.cancelButton
                    ) : (
                      <Button variant="secondary">
                        {typeof props.cancelButton === "string" ? props.cancelButton : "Dismiss"}
                      </Button>
                    )}
                  </A.Cancel>
                )}
                {isValidElement(props.confirmButton) ? (
                  props.confirmButton
                ) : (
                  <A.Action asChild>
                    <Button type="submit" disabled={confirming} loading={confirming}>
                      {typeof props.confirmButton === "string" ? props.confirmButton : "Confirm"}
                    </Button>
                  </A.Action>
                )}
              </ButtonGroup>
            </div>
          </form>
        </A.Content>
      </A.Portal>
    </A.Root>
  )
}

const alertStyles = tv({
  slots: {
    overlay: "radix-backdrop",
    content: [
      "@container radix-modal-animation",
      "fixed z-50 left-1/2 top-1/2 max-h-[85vh] w-[90vw] max-w-[500px] -translate-x-1/2 -translate-y-1/2",
      "rounded-sm bg-white shadow-md focus:outline-none",
      "flex flex-col",
    ],
    title: "type-title-s",
    description: "type-body",
    buttons: "flex justify-end gap-[25px]",
    header: "py-4 px-5 flex-none",
    inner: "p-5 border-t border-grey2 overflow-auto flex-1",
    footer: "px-4 py-5 border-t border-grey2 flex-none",
  },
})
